import type { MarketMonitorSourceScope } from './index';

export const INSTAGRAM_DISCOVERY_CAPABILITY = 'instagram.posts.discover_by_account';
export const DISCOVERY_DATE_SUPPORT = 'instagram-discovery-dates/v1';
export const YOUTUBE_DISCOVERY_DATE_SUPPORT = 'youtube-discovery-dates/v1';
const dateCapabilities = {
    [INSTAGRAM_DISCOVERY_CAPABILITY]: 'instagram',
    'youtube.videos.discover_by_keyword': 'youtube',
    'youtube.videos.discover_by_channel': 'youtube',
} as const;
export const isDiscoveryDateCapability = (capability: string): boolean => Object.prototype.hasOwnProperty.call(dateCapabilities, capability);
export interface DiscoveryDates {
    discovery_start_date?: string | null;
    discovery_end_date?: string | null;
}
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const calendarDate = (value: unknown): value is string => {
    if (typeof value !== 'string' || !/^(?!0000)\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const date = new Date(value + 'T00:00:00.000Z');
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

/** Native calendar days are not the local result filter's UTC boundaries. */
export function normalizeDiscoveryDates(value: DiscoveryDates): DiscoveryDates {
    const start = value.discovery_start_date, end = value.discovery_end_date;
    if (start == null && end == null) return {};
    if (!calendarDate(start) || !calendarDate(end) || start >= end) throw new Error('Invalid discovery dates');
    return { discovery_start_date: start, discovery_end_date: end };
}

export function validDiscoveryDateScopes(scopes: unknown): boolean {
    if (scopes === undefined) return true;
    if (!Array.isArray(scopes)) return false;
    try {
        return scopes.every(scope => {
            if (!record(scope)) return false;
            const pair = normalizeDiscoveryDates(scope);
            return !pair.discovery_start_date || typeof scope.capability === 'string' && isDiscoveryDateCapability(scope.capability)
                && dateCapabilities[scope.capability as keyof typeof dateCapabilities] === scope.source_system;
        });
    } catch { return false; }
}

/** Explicit null negotiates clear; previous scopes negotiate removal or a change of discovery mode. */
export const requestsDiscoveryDates = (scopes: MarketMonitorSourceScope[]) => scopes.some(scope =>
    scope.discovery_start_date !== undefined || scope.discovery_end_date !== undefined);

export function discoveryDateScopesMatch(requested: MarketMonitorSourceScope[], echoed: unknown,
    previous: MarketMonitorSourceScope[] = []): boolean {
    if (!validDiscoveryDateScopes(requested) || !validDiscoveryDateScopes(echoed)) return false;
    const actual = Array.isArray(echoed) ? echoed : [];
    if (!requestsDiscoveryDates(requested) && !requestsDiscoveryDates(previous) && !requestsDiscoveryDates(actual)) return true;
    if (!Array.isArray(echoed)) return false;
    const dates = (scopes: MarketMonitorSourceScope[]) => scopes.filter(scope => isDiscoveryDateCapability(scope.capability)).map(scope => {
        if (!record(scope.scope)) throw new Error('Missing discovery scope');
        return JSON.stringify([scope.capability, scope.source_system, scope.scope.kind, scope.scope.value ?? null, normalizeDiscoveryDates(scope)]);
    }).sort();
    try {
        const expected = dates(requested), received = dates(actual);
        return new Set(expected).size === expected.length && new Set(received).size === received.length
            && JSON.stringify(expected) === JSON.stringify(received);
    } catch { return false; }
}

export function discoveryDatePreviewMatches(requested: MarketMonitorSourceScope[], preview: {
    normalized_monitor: Record<string, unknown>; discovery_date_range_support?: unknown; youtube_discovery_date_range_support?: unknown;
}, previous: MarketMonitorSourceScope[] = []): boolean {
    const affected = [...requested, ...previous];
    const needsSupport = (source: string) => requestsDiscoveryDates(affected.filter(scope => scope.source_system === source));
    return (!needsSupport('instagram') || preview.discovery_date_range_support === DISCOVERY_DATE_SUPPORT)
        && (!needsSupport('youtube') || preview.youtube_discovery_date_range_support === YOUTUBE_DISCOVERY_DATE_SUPPORT)
        && discoveryDateScopesMatch(requested, preview.normalized_monitor.source_scopes, previous);
}
